import React, { useState, useMemo } from "react";
import styled from "styled-components";
import { FiPlus, FiTrash2, FiEdit3, FiChevronLeft, FiChevronRight } from "react-icons/fi";

const fmtYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parseDate = (str) => { const d = new Date(str); return new Date(d.getFullYear(), d.getMonth(), d.getDate()); };
const isSameDay = (a, b) => fmtYMD(a) === fmtYMD(b);
const formatDate = (str) => {
  if (!str) return "—";
  try { const d = new Date(str); const m = ["ENE","FEB","MAR","ABR","MAY","JUN","JUL","AGO","SEP","OCT","NOV","DEC"]; return `${d.getDate()} ${m[d.getMonth()]} ${d.getFullYear()}`; }
  catch { return str; }
};
const monthNames = ["ENERO","FEBRERO","MARZO","ABRIL","MAYO","JUNIO","JULIO","AGOSTO","SEPTIEMBRE","OCTUBRE","NOVIEMBRE","DICIEMBRE"];

const AdminCalendar = ({ periods = [], milestones = [], onAddPeriod, onUpdatePeriod, onRemovePeriod, onAddMilestone, onUpdateMilestone, onRemoveMilestone }) => {
  const [viewDate, setViewDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [editPeriodIdx, setEditPeriodIdx] = useState(null);

  const weeks = useMemo(() => {
    const y = viewDate.getFullYear(), m = viewDate.getMonth();
    const start = new Date(y, m, 1), end = new Date(y, m + 1, 0);
    const startDow = (start.getDay() + 6) % 7;
    const adj = new Date(start); adj.setDate(adj.getDate() - startDow);
    const w = []; let d = new Date(adj);
    while (w.length < 6) {
      const week = [];
      for (let i = 0; i < 7; i++) { week.push(d >= start && d <= end ? new Date(d) : null); d = new Date(d.getTime() + 86400000); }
      w.push(week);
      if (d > end && w.length >= 4) break;
    }
    return w;
  }, [viewDate]);

  const getPeriodColor = (date) => {
    if (!date) return null;
    const d = date.getTime();
    let color = null;
    for (const p of periods) {
      const s = parseDate(p.startDate).getTime(), e = parseDate(p.endDate).getTime();
      if (d >= s && d <= e) color = p.color;
    }
    return color;
  };

  const hasMilestone = (date) => {
    if (!date) return false;
    return milestones.some((m) => isSameDay(parseDate(m.date), date));
  };

  const selectedPeriodIndices = selectedDate
    ? periods.reduce((acc, p, idx) => {
        const d = selectedDate.getTime();
        if (d >= parseDate(p.startDate).getTime() && d <= parseDate(p.endDate).getTime()) acc.push(idx);
        return acc;
      }, [])
    : [];

  const selectedMilestoneIndices = selectedDate
    ? milestones.reduce((acc, m, idx) => {
        if (isSameDay(parseDate(m.date), selectedDate)) acc.push(idx);
        return acc;
      }, [])
    : [];

  const today = new Date();
  const todayStr = fmtYMD(today);

  return (
    <Wrapper>
      <CalendarHeader>
        <HeaderLeft>
          <NavBtn onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))}><FiChevronLeft /></NavBtn>
          <MonthLabel>{monthNames[viewDate.getMonth()]} {viewDate.getFullYear()}</MonthLabel>
          <NavBtn onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))}><FiChevronRight /></NavBtn>
        </HeaderLeft>
      </CalendarHeader>

      <Grid>
        {["L","M","X","J","V","S","D"].map((d) => <DayHeader key={d}>{d}</DayHeader>)}
        {weeks.map((week, wi) =>
          week.map((date, di) => {
            const key = `${wi}-${di}`;
            if (!date) return <Cell key={key} />;
            const color = getPeriodColor(date);
            const isToday = fmtYMD(date) === todayStr;
            const isSelected = selectedDate && isSameDay(date, selectedDate);
            const hasM = hasMilestone(date);
            return (
              <Cell
                key={key}
                $clickable
                $isToday={isToday}
                $isSelected={isSelected}
                onClick={() => { setSelectedDate(date); setEditPeriodIdx(null); }}
              >
                {color && <PeriodBg $color={color} />}
                <DayNum $isSelected={isSelected}>{date.getDate()}</DayNum>
                {hasM && <MilestoneDot />}
              </Cell>
            );
          })
        )}
      </Grid>

      {selectedDate && (
        <DetailPanel>
          <DetailHeader>
            <DetailDate>{formatDate(fmtYMD(selectedDate))}</DetailDate>
            <DetailActions>
              <SmallBtn onClick={() => { onAddPeriod(selectedDate.toISOString()); setEditPeriodIdx(periods.length); }}><FiPlus /> PERIODO</SmallBtn>
              <SmallBtn onClick={() => onAddMilestone(selectedDate.toISOString())}><FiPlus /> HITO</SmallBtn>
            </DetailActions>
          </DetailHeader>

          {selectedPeriodIndices.length > 0 && (
            <DetailSection>
              <DetailSectionTitle>PERIODOS ACTIVOS</DetailSectionTitle>
              {selectedPeriodIndices.map((idx) => {
                const p = periods[idx];
                const editing = editPeriodIdx === idx;
                return (
                  <DetailCard key={idx}>
                    <DetailCardHeader>
                      <DetailCardTitleRow>
                        <ColorSwatch $color={p.color || "#000"} />
                        <DetailCardName>{p.name || "SIN NOMBRE"}</DetailCardName>
                        <DetailRange>{formatDate(p.startDate)} → {formatDate(p.endDate)}</DetailRange>
                      </DetailCardTitleRow>
                      <DetailCardActions>
                        <IconBtnSmall onClick={() => setEditPeriodIdx(editing ? null : idx)}><FiEdit3 /></IconBtnSmall>
                        <IconBtnSmall $danger onClick={() => onRemovePeriod(idx)}><FiTrash2 /></IconBtnSmall>
                      </DetailCardActions>
                    </DetailCardHeader>
                    {editing && (
                      <EditForm>
                        <EditField>
                          <EditLabel>NAME</EditLabel>
                          <TextInput value={p.name || ""} onChange={(e) => onUpdatePeriod(idx, "name", e.target.value)} />
                        </EditField>
                        <EditField>
                          <EditLabel>START</EditLabel>
                          <TextInput type="datetime-local" value={p.startDate ? p.startDate.substring(0, 16) : ""} onChange={(e) => onUpdatePeriod(idx, "startDate", e.target.value)} />
                        </EditField>
                        <EditField>
                          <EditLabel>END</EditLabel>
                          <TextInput type="datetime-local" value={p.endDate ? p.endDate.substring(0, 16) : ""} onChange={(e) => onUpdatePeriod(idx, "endDate", e.target.value)} />
                        </EditField>
                        <EditField>
                          <EditLabel>COLOR</EditLabel>
                          <ColorPicker type="color" value={p.color || "#000000"} onChange={(e) => onUpdatePeriod(idx, "color", e.target.value)} />
                        </EditField>
                      </EditForm>
                    )}
                  </DetailCard>
                );
              })}
            </DetailSection>
          )}

          {selectedMilestoneIndices.length > 0 && (
            <DetailSection>
              <DetailSectionTitle>HITOS</DetailSectionTitle>
              {selectedMilestoneIndices.map((idx) => {
                const m = milestones[idx];
                return (
                  <DetailCard key={idx}>
                    <MilestoneEditContent
                      milestone={m}
                      idx={idx}
                      onUpdateMilestone={onUpdateMilestone}
                      onRemoveMilestone={onRemoveMilestone}
                    />
                  </DetailCard>
                );
              })}
            </DetailSection>
          )}

          {selectedPeriodIndices.length === 0 && selectedMilestoneIndices.length === 0 && (
            <EmptyDetail>
              <p>No hay nada en esta fecha.</p>
            </EmptyDetail>
          )}
        </DetailPanel>
      )}

      {!selectedDate && (
        <SummaryRow>
          <SummaryStat><StatNum>{periods.length}</StatNum> PERIODOS</SummaryStat>
          <SummaryStat><StatNum>{milestones.length}</StatNum> HITOS</SummaryStat>
          <SummaryHint>Selecciona un día para ver y editar</SummaryHint>
        </SummaryRow>
      )}
    </Wrapper>
  );
};

// Milestone sub-component
const MilestoneEditContent = ({ milestone, idx, onUpdateMilestone, onRemoveMilestone }) => {
  const [editing, setEditing] = useState(false);
  return (
    <>
      <DetailCardHeader>
        <DetailCardTitleRow>
          <GoldDot />
          <DetailCardName>{milestone.name || "SIN NOMBRE"}</DetailCardName>
          <DetailRange>{formatDate(milestone.date)}</DetailRange>
        </DetailCardTitleRow>
        <DetailCardActions>
          <IconBtnSmall onClick={() => setEditing(!editing)}><FiEdit3 /></IconBtnSmall>
          <IconBtnSmall $danger onClick={() => onRemoveMilestone(idx)}><FiTrash2 /></IconBtnSmall>
        </DetailCardActions>
      </DetailCardHeader>
      {editing && (
        <EditForm>
          <EditField>
            <EditLabel>NAME</EditLabel>
            <TextInput value={milestone.name || ""} onChange={(e) => onUpdateMilestone(idx, "name", e.target.value)} />
          </EditField>
          <EditField>
            <EditLabel>DATE</EditLabel>
            <TextInput type="datetime-local" value={milestone.date ? milestone.date.substring(0, 16) : ""} onChange={(e) => onUpdateMilestone(idx, "date", e.target.value)} />
          </EditField>
        </EditForm>
      )}
    </>
  );
};

// Styled Components
const Wrapper = styled.div`width: 100%;`;

const CalendarHeader = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; flex-wrap: wrap; gap: 0.75rem;`;
const HeaderLeft = styled.div`display: flex; align-items: center; gap: 0.5rem;`;
const MonthLabel = styled.span`font-family: var(--v-font-body); font-size: 18px; font-weight: 600; color: var(--v-fg); min-width: 150px; text-align: center;`;
const NavBtn = styled.button`display: flex; align-items: center; justify-content: center; width: 2rem; height: 2rem; border-radius: 50%; border: 1px solid rgba(255,255,255,0.1); background: transparent; color: var(--v-fg); cursor: pointer; transition: border-color 0.15s ease; &:hover { border-color: var(--v-mint); }`;

const SmallBtn = styled.button`display: inline-flex; align-items: center; gap: 0.3rem; background: rgba(60,255,208,0.08); border: 1px solid rgba(60,255,208,0.2); color: var(--v-mint); font-family: var(--v-font-mono); font-size: 8px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; padding: 0.3rem 0.65rem; border-radius: 16px; cursor: pointer; transition: background 0.15s ease; white-space: nowrap; &:hover { background: rgba(60,255,208,0.14); }`;

const Grid = styled.div`display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); border-radius: 16px; padding: 0.5rem;`;

const DayHeader = styled.div`text-align: center; font-family: var(--v-font-mono); font-size: 9px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: var(--v-fg-muted); padding: 0.4rem 0;`;

const Cell = styled.div`
  position: relative; aspect-ratio: 1; display: flex; align-items: center; justify-content: center;
  border-radius: 12px; cursor: ${({ $clickable }) => $clickable ? "pointer" : "default"};
  background: ${({ $isSelected }) => $isSelected ? "rgba(60,255,208,0.08)" : "transparent"};
  border: 1px solid ${({ $isSelected }) => $isSelected ? "var(--v-mint)" : "transparent"};
  outline: ${({ $isToday }) => $isToday ? "1px solid rgba(201,160,78,0.3)" : "none"};
  transition: background 0.1s ease;
  overflow: hidden;
  &:hover { background: ${({ $clickable }) => $clickable ? "rgba(255,255,255,0.04)" : "transparent"}; }
`;

const PeriodBg = styled.div`position: absolute; inset: 2px; border-radius: 10px; background: ${({ $color }) => $color}22; border: 1px solid ${({ $color }) => $color}44;`;

const DayNum = styled.span`position: relative; z-index: 1; font-family: var(--v-font-body); font-size: 12px; font-weight: 500; color: ${({ $isSelected }) => $isSelected ? "var(--v-mint)" : "var(--v-fg)"};`;

const MilestoneDot = styled.div`position: absolute; bottom: 4px; left: 50%; transform: translateX(-50%); width: 4px; height: 4px; border-radius: 50%; background: var(--v-gold-bright, #c9a04e); z-index: 1;`;

const DetailPanel = styled.div`margin-top: 1rem; background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.04); border-radius: 16px; padding: 1rem;`;

const DetailHeader = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.75rem; flex-wrap: wrap; gap: 0.5rem;`;
const DetailDate = styled.div`font-family: var(--v-font-body); font-size: 16px; font-weight: 600; color: var(--v-fg);`;
const DetailActions = styled.div`display: flex; gap: 0.4rem;`;

const DetailSection = styled.div`margin-bottom: 1rem; &:last-child { margin-bottom: 0; }`;

const DetailSectionTitle = styled.div`font-family: var(--v-font-mono); font-size: 9px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: var(--v-fg-muted); margin-bottom: 0.5rem;`;

const DetailCard = styled.div`background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.04); border-radius: 12px; padding: 0.65rem 0.75rem; margin-bottom: 0.4rem;`;

const DetailCardHeader = styled.div`display: flex; justify-content: space-between; align-items: center; gap: 0.5rem;`;

const DetailCardTitleRow = styled.div`display: flex; align-items: center; gap: 0.5rem; flex: 1; min-width: 0;`;

const ColorSwatch = styled.div`width: 10px; height: 10px; border-radius: 4px; background: ${({ $color }) => $color}; flex-shrink: 0;`;

const GoldDot = styled.div`width: 8px; height: 8px; border-radius: 50%; background: var(--v-gold-bright, #c9a04e); flex-shrink: 0;`;

const DetailCardName = styled.span`font-family: var(--v-font-body); font-size: 13px; font-weight: 500; color: var(--v-fg); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`;

const DetailRange = styled.span`font-family: var(--v-font-mono); font-size: 8px; color: var(--v-fg-muted); white-space: nowrap;`;

const DetailCardActions = styled.div`display: flex; gap: 0.3rem; flex-shrink: 0;`;

const IconBtnSmall = styled.button`display: flex; align-items: center; justify-content: center; width: 1.6rem; height: 1.6rem; border-radius: 50%; border: 1px solid ${({ $danger }) => $danger ? "rgba(255,59,59,0.2)" : "rgba(255,255,255,0.1)"}; background: transparent; color: ${({ $danger }) => $danger ? "#ff3b3b" : "var(--v-fg-muted)"}; cursor: pointer; flex-shrink: 0; transition: border-color 0.15s ease; font-size: 11px; &:hover { border-color: ${({ $danger }) => $danger ? "#ff3b3b" : "var(--v-mint)"}; }`;

const EditForm = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: 0.4rem; margin-top: 0.6rem; padding-top: 0.6rem; border-top: 1px solid rgba(255,255,255,0.04); @media (max-width: 640px) { grid-template-columns: 1fr; }`;

const EditField = styled.div``;
const EditLabel = styled.label`display: block; font-family: var(--v-font-mono); font-size: 8px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: var(--v-fg-muted); margin-bottom: 0.2rem;`;

const TextInput = styled.input`width: 100%; padding: 0.4rem 0.6rem; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; color: var(--v-fg); font-family: var(--v-font-body); font-size: 12px; outline: none; transition: border-color 0.15s ease; box-sizing: border-box; &:focus { border-color: var(--v-mint); }`;

const ColorPicker = styled.input`width: 100%; height: 2rem; padding: 0.2rem; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; cursor: pointer; box-sizing: border-box;`;

const EmptyDetail = styled.div`text-align: center; padding: 1rem; p { font-family: var(--v-font-body); font-size: 12px; color: var(--v-fg-muted); margin: 0 0 0.75rem; }`;

const SummaryRow = styled.div`margin-top: 0.75rem; display: flex; align-items: center; gap: 1.5rem;`;
const SummaryStat = styled.div`font-family: var(--v-font-mono); font-size: 9px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: var(--v-fg-muted);`;
const StatNum = styled.span`font-family: var(--v-font-body); font-size: 18px; font-weight: 700; color: var(--v-fg); margin-right: 0.3rem;`;
const SummaryHint = styled.span`font-family: var(--v-font-body); font-size: 11px; color: var(--v-fg-muted); opacity: 0.6;`;

export default AdminCalendar;
